import { useCallback, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useLocation, useMatches, useNavigate, useSearch } from '@tanstack/react-router'

import { Button } from '@gto/ui/button'
import { Paper } from '@gto/ui/paper'
import { colors } from '@gto/ui/theme/design-tokens'
import { Typography } from '@gto/ui/typography'

import type { Deal } from '@/api/gen'
import { SiteLogo } from '@/components/site-logo'
import { useAuth } from '@/hooks/use-auth'
import type { Translations } from '@/localise'
import { ReportUsernameModal } from '@/pages/deals/components/report-username-modal'
import { useUserPromoStatus } from '@/pages/promotion/hooks/use-user-promo-status'

export const PromoDealCard = ({
  poker_site_slug,
  poker_site_local_s3_url,
  poker_site_name,
  deal_id,
  signup_url,
}: Deal) => {
  const infoItemsCount = 3
  const navigate = useNavigate()
  const matches = useMatches()
  const [showModal, setShowModal] = useState(false)
  const { isAuthorized, user } = useAuth()
  const { pathname } = useLocation()
  const { tickets, loading } = useUserPromoStatus()
  const promoOverviewRoute = matches.some((m) => {
    return typeof m.routeId === 'string' && m.routeId === '/_layout/promotion/august-2025/'
  })
  const freeTicketsRoute = matches.some((m) => {
    return (
      typeof m.routeId === 'string' &&
      m.routeId === '/_layout/promotion/august-2025/_tabs/free-tournament-tickets'
    )
  })

  const search = useSearch({
    from: promoOverviewRoute
      ? '/_layout/promotion/august-2025/'
      : '/_layout/promotion/august-2025/_tabs/free-tournament-tickets',
  })

  const buildSignUpUrl = useCallback(
    (url: string, param: string) => {
      if (user === null) {
        return
      }

      const urlObj = new URL(url)
      switch (param) {
        case 'coinpoker':
          urlObj.searchParams.set('payload', `rm2-${user.user_id}`)
          break

        case 'redstar-poker':
        case 'red-star-poker':
          urlObj.searchParams.set('ext', `o6t-${user.user_id}`)
          break
        case 'americas-cardroom':
        case 'americas-cardroom-acr':
          urlObj.searchParams.set('afp', `kwo-${user.user_id}`)
          break

        default:
          break
      }

      return urlObj.toString()
    },
    [user],
  )

  const onToggleModal = () => {
    setShowModal((prev) => !prev)
  }

  const onStartSignUp = useCallback(
    (url: string) => {
      setShowModal(true)
      const urlToOpen = buildSignUpUrl(url, poker_site_slug)
      window.open(urlToOpen, '_blank', 'noopener,noreferrer')

      navigate({
        to: '.',
        search: {},
      }).catch(console.error)
    },
    [buildSignUpUrl, navigate, poker_site_slug],
  )

  const onSignUp = () => {
    if (isAuthorized) {
      // oxlint-disable-next-line typescript/no-unnecessary-condition
      if (signup_url !== null && user !== null) {
        onStartSignUp(signup_url)
      }
    } else {
      const params = new URLSearchParams()
      params.set('report', 'true')
      params.set('deal', deal_id)
      // oxlint-disable-next-line typescript/no-unnecessary-condition
      if (signup_url !== null) {
        params.set('deal_url', signup_url)
      }

      const backUrl = `${pathname}?${params.toString()}`
      navigate({
        to: '/login',
        search: {
          back_url: backUrl,
        },
      }).catch(console.error)
    }
  }

  const shouldResumeSignUpFromDealParams =
    isAuthorized && search.deal != null && search.report != null && search.deal === deal_id

  useEffect(
    function autoStartSignUpFromDealParams() {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- resume-after-login, not an event on this page: the user clicked Sign Up while unauthorized and was redirected to /login carrying the deal params; on return, arriving authorized with matching deal/deal_url/report params auto-continues the signup. The originating click happened on a prior mount, so there is no handler here; window.open + the URL rewrite need live `user`/auth state a route loader can't run pre-render.
      if (shouldResumeSignUpFromDealParams && search.deal_url != null) {
        onStartSignUp(search.deal_url)
      }
    },
    [shouldResumeSignUpFromDealParams, onStartSignUp, search.deal_url],
  )

  return (
    <article className="flex flex-[0_0_calc(33.33%-11px)] flex-col overflow-hidden rounded-md border border-divider bg-paper-elevation3 md:flex-[1_0_48%] sm:flex-[1_0_100%]">
      <div
        className="flex items-center gap-4 px-4 py-2 md:gap-2 sm:p-4"
        style={{ background: colors.paper.elevation5 }}
      >
        <SiteLogo
          logo={poker_site_local_s3_url}
          name={poker_site_name}
          size="small"
        />
        <Typography
          className="w-full text-[1.5rem] font-semibold leading-[1.6] md:text-[1.25rem] md:leading-[1.2]"
          variant="h6"
        >
          {poker_site_name}
        </Typography>
      </div>
      <div className="flex flex-[1_0_auto] flex-col gap-4 p-3">
        <Paper
          className="shrink-0 grow-[1] basis-auto rounded-md px-4 py-2 sm:p-4"
          elevation="2"
        >
          <ul className="m-0 list-inside list-disc p-0 text-text-secondary">
            {Array(infoItemsCount)
              .fill(null)
              .map((_, index) => (
                <li
                  // eslint-disable-next-line @eslint-react/no-array-index-key -- fixed 3 info rows, never reorders
                  key={index}
                >
                  <Typography
                    color="text.secondary"
                    variant="body3"
                  >
                    <FormattedMessage
                      id={`promo.offers.${poker_site_slug}.info.${index}` as keyof Translations}
                    />
                  </Typography>
                </li>
              ))}
          </ul>
        </Paper>
        {tickets?.sites[poker_site_slug]?.verified === true ? (
          <Button
            disabled
            color="secondary"
            size="large"
            variant="contained"
          >
            <FormattedMessage id="common.controls.accountCreated" />
          </Button>
        ) : (
          <>
            <Button
              color="warning"
              loading={loading}
              size="large"
              variant="contained"
              onClick={onSignUp}
            >
              <FormattedMessage id="common.controls.signUp" />
            </Button>
            {!freeTicketsRoute && (
              <Button
                color="secondary"
                loading={loading}
                size="large"
                variant="contained"
                onClick={() => {
                  navigate({
                    to: `/app/deals/$room`,
                    params: { room: poker_site_slug },

                    hash: 'info',
                  }).catch(console.error)
                }}
              >
                <FormattedMessage id="common.controls.moreDetails" />
              </Button>
            )}
          </>
        )}
      </div>
      <ReportUsernameModal
        dealId={deal_id}
        open={showModal}
        room={poker_site_slug}
        onClose={onToggleModal}
      />
    </article>
  )
}
